import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Experience.css';
import DOMPurify from 'dompurify';

const Experience = () => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [dragDirection, setDragDirection] = useState('right');
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const timelineRef = useRef(null);
  const containerRef = useRef(null);

  const experiences = t('experience.positions', { returnObjects: true }) || [];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftButton(scrollLeft > 20);
      setShowRightButton(scrollLeft < scrollWidth - clientWidth - 20);
      
      // Update active index based on scroll position
      const itemWidth = clientWidth;
      const newIndex = Math.round(scrollLeft / itemWidth);
      setActiveIndex(newIndex);
    };

    container.addEventListener('scroll', checkScroll);
    checkScroll();

    return () => container.removeEventListener('scroll', checkScroll);
  }, []);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    setMousePos({ x: e.clientX, y: e.clientY });
    
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = x - startX;
    setDragDirection(walk < 0 ? 'right' : 'left');
    
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setStartX(touch.pageX);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const currentX = touch.pageX;
    const diff = startX - currentX;
    const sensitivity = 1.5;
    
    containerRef.current.scrollLeft = scrollLeft + (diff * sensitivity);
    setDragDirection(diff > 0 ? 'right' : 'left');
    setMousePos({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = (e) => {
    const container = containerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    const touch = e.changedTouches[0];
    const endX = touch.pageX;
    const distance = startX - endX;
    const speed = Math.abs(distance) / 100;
    
    if (Math.abs(distance) > 50) {
      const targetScroll = scrollLeft + (distance * speed * 2);
      const maxScroll = scrollWidth - clientWidth;
      
      container.scrollTo({
        left: Math.max(0, Math.min(targetScroll, maxScroll)),
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = (direction) => {
    const container = containerRef.current;
    const itemWidth = container.clientWidth;
    const newIndex = direction === 'left' ? activeIndex - 1 : activeIndex + 1;
    
    if (newIndex >= 0 && newIndex < experiences.length) {
      setActiveIndex(newIndex);
      container.scrollTo({
        left: itemWidth * newIndex,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="experience" className="experience">
      <div className="experience-content">
        <h2 className="section-title" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('experience.title')) }} />
        
        <div className="carousel-container">
          <div className="timeline-controls">
            {showLeftButton && (
              <button 
                className="timeline-button prev" 
                onClick={() => handleScroll('left')}
                aria-label="Previous experience"
              >
                ←
              </button>
            )}
            {showRightButton && (
              <button 
                className="timeline-button next" 
                onClick={() => handleScroll('right')}
                aria-label="Next experience"
              >
                →
              </button>
            )}
          </div>

          <div 
            className="timeline-container"
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="timeline-wrapper" ref={timelineRef}>
              {experiences.map((exp, index) => (
                <div 
                  key={index} 
                  className={`timeline-item ${index === activeIndex ? 'active' : ''}`}
                  style={{ '--index': index }}
                >
                  <div className="timeline-year" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(exp.period) }} />
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <h3 className="company-name" 
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(exp.company) 
                        }} 
                      />
                      <h4 className="role-title"
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(exp.title) 
                        }} 
                      />
                    </div>
                    
                    <div className="timeline-details">
                      <ul className="description-list">
                        {(exp.description || []).map((item, i) => (
                          <li key={i} className="description-item"
                            dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(item) 
                            }} 
                          />
                        ))}
                      </ul>
                      
                      {exp.techStack && (
                        <div className="tech-stack">
                          {Object.entries(exp.techStack).map(([category, technologies]) => (
                            <div key={category} className="tech-category">
                              <h5 className="tech-category-title"
                                dangerouslySetInnerHTML={{ 
                                  __html: DOMPurify.sanitize(category) 
                                }} 
                              />
                              <div className="tech-list">
                                {technologies.map((tech, i) => (
                                  <span key={i} className="tech-item"
                                    dangerouslySetInnerHTML={{ 
                                      __html: DOMPurify.sanitize(tech) 
                                    }} 
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="carousel-progress">
            {experiences.map((_, index) => (
              <button
                key={index}
                className={`progress-dot ${index === activeIndex ? 'active' : ''}`}
                onClick={() => {
                  const container = containerRef.current;
                  const itemWidth = container.clientWidth;
                  setActiveIndex(index);
                  container.scrollTo({
                    left: itemWidth * index,
                    behavior: 'smooth'
                  });
                }}
                aria-label={`Go to experience ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
